import type { Ticket } from "../data/api-types";
import { daysAgo, mockToday as today } from "./today";

// Customer/job/estimate/alert/opportunity ids below are cross-checked
// against the real mock fixtures they resolve through (customers.ts,
// bookedOrders.ts, estimates.ts, alerts.ts, opportunities.ts) — a ticket
// linking to an id that doesn't exist elsewhere silently shows no
// customer/linked-entity info in the UI, so every subject_id here is real.
export const mockTickets: Ticket[] = [
  {
    id: "ticket_001",
    title: "Call Bramley & Co",
    customer_id: "cust_012",
    // Same contact as ticket_015's campaign-engaged ticket — demonstrates
    // the ticket page's "related tickets for this contact" section.
    person_id: "per_001",
    // Connection: opportunity takes precedence over alert (decision B).
    subject_type: "opportunity",
    subject_id: "opp_001",
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "call",
    due_date: today,
    created_at: `${today}T09:00:00Z`,
    priority: "normal",
    category_id: "cat_sales_followup",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_002",
    title: "Send packaging proposal to Business School UK",
    customer_id: "cust_048",
    subject_type: "opportunity",
    subject_id: "opp_002",
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "email",
    due_date: today,
    created_at: `${today}T09:30:00Z`,
    priority: "high",
    category_id: "cat_sales_followup",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_003",
    title: "Investigate late delivery for TechStart Ltd",
    description:
      "Customer called chasing the trade-show banners job — courier tracking shows no movement since yesterday.",
    customer_id: "cust_022",
    subject_type: "job",
    subject_id: "ord_003",
    subject_label: null,
    assigned_to_user_id: "user_002",
    created_by_user_id: "user_001",
    action_type: "call",
    due_date: today,
    created_at: `${today}T08:15:00Z`,
    priority: "urgent",
    category_id: "cat_delivery",
    resolved_at: null,
    closed_at: null,
    status: "in_progress",
  },
  {
    id: "ticket_004",
    title: "Chase signed artwork approval before we can proceed",
    description:
      "Waiting on Business School UK to confirm the annual report proof before booking on.",
    customer_id: "cust_048",
    subject_type: "estimate",
    // estimate_header_ref, not the row id — the ticket tracks the enquiry,
    // resolved to whichever revision is current (see getCurrentEstimateByHeaderRef).
    subject_id: "HDR-2026-0041",
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "email",
    due_date: null,
    created_at: `${today}T07:40:00Z`,
    priority: "normal",
    category_id: "cat_estimating",
    resolved_at: null,
    closed_at: null,
    status: "waiting",
  },
  {
    id: "ticket_005",
    title: "Billing query resolved for Henderson Group",
    description:
      "Customer disputed an invoice line — confirmed correct and explained the surcharge.",
    customer_id: "cust_073",
    subject_type: null,
    subject_id: null,
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "call",
    due_date: null,
    created_at: "2026-07-10T11:00:00Z",
    priority: "low",
    category_id: "cat_billing",
    resolved_at: "2026-07-11T09:30:00Z",
    closed_at: null,
    status: "resolved",
  },
  {
    id: "ticket_006",
    title: "Reprint agreed for damaged stock — TechStart Ltd",
    description:
      "Boxes arrived crushed in transit. Reprint approved and dispatched.",
    customer_id: "cust_022",
    subject_type: null,
    subject_id: null,
    subject_label: null,
    assigned_to_user_id: "user_002",
    created_by_user_id: "user_002",
    action_type: "general",
    due_date: null,
    created_at: "2026-07-05T13:20:00Z",
    priority: "high",
    category_id: "cat_quality",
    resolved_at: "2026-07-06T10:00:00Z",
    closed_at: "2026-07-09T10:00:00Z",
    status: "closed",
  },
  {
    id: "ticket_007",
    title: "Follow up on packaging expansion alert",
    description:
      "Alert flagged Business School UK as a fit for adjacent packaging work — reach out to gauge interest.",
    customer_id: "cust_048",
    subject_type: "alert",
    subject_id: "alert_002",
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "email",
    due_date: today,
    created_at: `${today}T10:05:00Z`,
    priority: "urgent",
    category_id: "cat_sales_followup",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_008",
    title: "Query about last statement from Henderson Group",
    description:
      "Left a voicemail asking about a query on their last statement — no urgency indicated.",
    customer_id: "cust_073",
    subject_type: null,
    subject_id: null,
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "call",
    due_date: null,
    created_at: "2026-07-14T15:00:00Z",
    priority: "low",
    category_id: "cat_billing",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_009",
    title: "Engagement growth follow-up for TechStart Ltd",
    description:
      "Signal shows TechStart's engagement trending up — checking in to see if there's more work to book.",
    customer_id: "cust_022",
    subject_type: "alert",
    subject_id: "alert_004",
    subject_label: null,
    assigned_to_user_id: "user_002",
    created_by_user_id: "user_002",
    action_type: "call",
    due_date: today,
    created_at: "2026-07-13T09:00:00Z",
    priority: "normal",
    category_id: null,
    resolved_at: null,
    closed_at: null,
    status: "in_progress",
  },
  {
    id: "ticket_010",
    title: "Confirm reprint quantity before rebooking",
    description:
      "Bramley & Co want to confirm the catalogue reprint quantity before we rebook — waiting on their reply.",
    customer_id: "cust_012",
    subject_type: "estimate",
    // estimate_header_ref, not the row id — see ticket_004 above.
    subject_id: "HDR-2026-0077",
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "email",
    due_date: null,
    created_at: "2026-07-12T14:00:00Z",
    priority: "normal",
    category_id: "cat_estimating",
    resolved_at: null,
    closed_at: null,
    status: "waiting",
  },
  {
    id: "ticket_011",
    title: "Growth opportunity actioned for Henderson Group",
    description:
      "Followed up on the flagged opportunity — customer confirmed interest, handed to sales for quoting.",
    customer_id: "cust_073",
    subject_type: "opportunity",
    subject_id: "opp_003",
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "call",
    due_date: null,
    created_at: "2026-07-08T10:00:00Z",
    priority: "normal",
    category_id: "cat_sales_followup",
    resolved_at: "2026-07-09T11:15:00Z",
    closed_at: null,
    status: "resolved",
  },
  {
    id: "ticket_012",
    title: "Post-delivery check-in — Business School UK catalogue",
    description:
      "Confirmed the summer product catalogue arrived on spec and on time after last month's job.",
    customer_id: "cust_048",
    subject_type: "job",
    subject_id: "ord_002",
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "email",
    due_date: null,
    created_at: "2026-06-20T09:00:00Z",
    priority: "low",
    category_id: "cat_delivery",
    resolved_at: "2026-06-21T09:00:00Z",
    closed_at: "2026-06-24T09:00:00Z",
    status: "closed",
  },
  // csr-ticket-routing demo tickets — auto-created from a production
  // job-lateness breach, not a CSR-triggered "convert to ticket" action
  // (see docs/features/csr-ticket-routing/01_product_brief.md). Both use
  // subject_type "job" (the ticket's dedupe key is the job, not the
  // triggering alert — see brief §9's correction), created_by "system"
  // matching the raise_at_risk_tickets sentinel-actor precedent.
  {
    id: "ticket_013",
    title: "Job 48291 overdue in Printing — Acme Ltd",
    description:
      "Automatically raised: the Annual Report 2024 job has passed its due date while still in Printing. Carol White (usual CSR) is on leave until Aug 3, so this was routed to her buddy.",
    customer_id: "cust_012",
    subject_type: "job",
    subject_id: "ord_001",
    subject_label: null,
    assigned_to_user_id: "user_002",
    created_by_user_id: "system",
    action_type: "general",
    due_date: today,
    created_at: `${today}T06:05:00Z`,
    priority: "high",
    category_id: "cat_delivery",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_014",
    title: "Job 61830 overdue in Prepress — Business School UK",
    description:
      "Automatically raised: prepress has missed its planned handoff and the job's own due date has now also passed. The owning CSR and every configured buddy were unavailable at the time this ticket was raised — needs manual pickup.",
    customer_id: "cust_048",
    subject_type: "job",
    subject_id: "ord_002",
    subject_label: null,
    assigned_to_user_id: null,
    created_by_user_id: "system",
    action_type: "general",
    due_date: today,
    created_at: `${today}T06:10:00Z`,
    priority: "urgent",
    category_id: "cat_delivery",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  // Marketing-campaign round-up tickets (raise_engagement_tickets /
  // raise_bounce_tickets) — the "campaign ticket noise" the Tickets page's
  // pinned Campaign tickets section exists to surface, distinct from the
  // job-lateness/at-risk auto-tickets above by subject_type.
  {
    id: "ticket_015",
    title: "Engaged with Spring Folder Push — Bramley & Co",
    description:
      "This contact opened or clicked the Spring Folder Push campaign email. Follow up while it's fresh.",
    customer_id: "cust_012",
    person_id: "per_001",
    subject_type: "campaign_engaged",
    subject_id: "cmp_001:per_001",
    subject_label: "Spring Folder Push",
    assigned_to_user_id: "user_001",
    created_by_user_id: "system",
    action_type: "call",
    due_date: today,
    created_at: `${today}T08:00:00Z`,
    // raise_engagement_tickets now always raises engaged tickets urgent.
    priority: "urgent",
    category_id: "cat_sales_followup",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_016",
    title: "Email bounced for Spring Folder Push — Business School UK",
    description:
      "The campaign email to this contact bounced. Their email address on file may be out of date.",
    customer_id: "cust_048",
    subject_type: "campaign_bounce",
    subject_id: "cmp_001:per_002",
    subject_label: "Spring Folder Push",
    assigned_to_user_id: "user_001",
    created_by_user_id: "system",
    action_type: "general",
    due_date: today,
    created_at: `${today}T08:05:00Z`,
    priority: "normal",
    category_id: "cat_delivery",
    resolved_at: null,
    closed_at: null,
    status: "open",
    // Demonstrates the "already opened" indicator in the ticket list.
    first_viewed_at: `${today}T08:30:00Z`,
  },
  {
    // A landing-page enquiry. It shares the pink box with campaign tickets
    // but is not campaign work: no campaign in subject_id, and
    // subject_label names the lead source rather than a campaign. Kept
    // here so the box's "Landing page" toggle has something real to show.
    id: "ticket_024",
    title: "Landing page lead — Big Pink Book",
    description:
      "'Priya Raman' <priya.raman@harlowpublishing.co.uk>, 01279 446231, Harlow Publishing — requested via Big Pink Book.\n\nThis is a new contact and has been added to Leads as 'Harlow Publishing'.",
    customer_id: null,
    subject_type: "landing_page_lead",
    subject_id: "leadsrc_big_pink_book:priya.raman@harlowpublishing.co.uk",
    subject_label: "Big Pink Book",
    assigned_to_user_id: "user_001",
    created_by_user_id: "system",
    action_type: "call",
    due_date: today,
    created_at: `${today}T09:10:00Z`,
    priority: "normal",
    category_id: null,
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  // Department-ticket-routing (departments.ts: Prepress - Exeter, IT) —
  // owning_department_id/escalates_at computed relative to real "now"
  // (not `today`, which stays a fixed calendar date), so the "escalates in
  // Xh" affordance always renders a plausible countdown regardless of when
  // Storybook/the app happens to run.
  {
    id: "ticket_017",
    title: "Job 61902 stuck at plate-out — Bramley & Co",
    description:
      "Prepress problem-run queue: the plate-out stage has been parked for review for longer than the routine sign-off window. Nobody in Prepress - Exeter has picked this up yet.",
    customer_id: "cust_012",
    subject_type: "job",
    subject_id: "ord_001",
    subject_label: null,
    assigned_to_user_id: null,
    owning_department_id: "dept_prepress_exeter",
    created_by_user_id: "system",
    action_type: "general",
    due_date: null,
    created_at: new Date(Date.now() - 22 * 60 * 60 * 1000).toISOString(),
    // ~2h out — exactly the "somebody should claim this" window the
    // affordance exists to surface (brief §11).
    escalates_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    priority: "high",
    category_id: null,
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_018",
    title: "Proof approval overdue — Business School UK",
    description:
      "Prepress problem-run queue: no one claimed this within the working-day threshold, so it escalated to the department lead.",
    customer_id: "cust_048",
    subject_type: "job",
    subject_id: "ord_002",
    subject_label: null,
    // Escalated: the sweep assigns the department lead once the threshold
    // passes (workflow_module's escalate_unclaimed_department_tickets).
    // owning_department_id stays set; escalates_at is null once assigned —
    // it's only ever computed for unclaimed tickets.
    assigned_to_user_id: "u5",
    owning_department_id: "dept_prepress_exeter",
    created_by_user_id: "system",
    action_type: "general",
    due_date: null,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    escalates_at: null,
    priority: "urgent",
    category_id: null,
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_019",
    title: "New starter needs a One login — IT",
    description:
      "Onboarding request routed to IT: nobody in the department has picked it up yet.",
    customer_id: null,
    subject_type: null,
    subject_id: null,
    subject_label: null,
    assigned_to_user_id: null,
    owning_department_id: "dept_it",
    created_by_user_id: "system",
    action_type: "general",
    due_date: null,
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    // Multi-day out — demonstrates the "in Xd" bucket alongside ticket_017's
    // "in 2h".
    escalates_at: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    priority: "normal",
    category_id: null,
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  // csr-command-centre rail fixtures — the CSR home's Tickets rail
  // (CommandCentreRail.tsx, sourced via csr-home.ts's portfolioTickets,
  // which projects a curated subset of this file's tickets rather than
  // hand-authoring its own) needs an overdue ticket and a job-linked ticket
  // assigned to the mock current user (user_001, see mock-adapter.ts's
  // MockAuthProvider comment) among its states. Neither existed among the
  // user_001-assigned active tickets above, so they're added here rather
  // than reintroduced as rail-local fixtures with their own ids.
  {
    id: "ticket_020",
    title: "Confirm signed PO before we invoice — Henderson Group",
    description:
      "Accounts flagged the purchase order for the last job as still unsigned — chase before it holds up the invoice run.",
    customer_id: "cust_073",
    subject_type: null,
    subject_id: null,
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "email",
    due_date: daysAgo(2),
    created_at: `${daysAgo(4)}T09:00:00Z`,
    priority: "normal",
    category_id: "cat_billing",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  {
    id: "ticket_021",
    title: "Chase artwork sign-off before job 48291 goes to press",
    description:
      "Bramley & Co asked for one more look at the annual report proof before we lock in press time. Tracked against the job's own due date rather than a separate deadline of its own.",
    customer_id: "cust_012",
    subject_type: "job",
    subject_id: "ord_001",
    subject_label: null,
    assigned_to_user_id: "user_001",
    created_by_user_id: "user_001",
    action_type: "call",
    due_date: null,
    created_at: `${today}T11:00:00Z`,
    priority: "normal",
    category_id: "cat_delivery",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  // ticket_022 — a campaign ticket already worked (see the matching comment
  // activity in ticketActivity.ts), demonstrating the "Actioned" box's
  // search-auto-expand behaviour (TicketsPage.stories.tsx). Deliberately a
  // different customer/subject_type than ticket_016's untouched-box fixture
  // so the two search stories don't collide.
  {
    id: "ticket_022",
    title: "Engaged — Henderson Group",
    description:
      "This contact engaged with the Spring Folder Push campaign email: 2 opens, 1 clicks. Call to gauge interest.",
    customer_id: "cust_073",
    subject_type: "campaign_engaged",
    subject_id: "cmp_001:per_073",
    subject_label: "Spring Folder Push",
    assigned_to_user_id: "user_001",
    created_by_user_id: "system",
    action_type: "call",
    due_date: null,
    created_at: `${today}T08:00:00Z`,
    priority: "urgent",
    category_id: "cat_sales_followup",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
  // ticket_023 — an earlier tranche of the *same* campaign (cmp_001, Spring
  // Folder Push) for the *same contact* as ticket_015 (per_001 / Bramley &
  // Co), already worked and resolved with a logged outcome. Demonstrates the
  // "related ticket already actioned" banner (TicketDetailPage) — a rep
  // opening ticket_015 (raised days later by the reminder tranche of the same
  // push) needs this outcome visible immediately, not just listed, so they
  // don't call a contact who was already spoken to. Title and subject_id are
  // deliberately identical to ticket_015's: the raiser generates them from
  // campaign + contact, so the two tranches are indistinguishable to the rep
  // from the list alone, which is precisely why the banner has to carry the
  // outcome.
  {
    id: "ticket_023",
    title: "Engaged with Spring Folder Push — Bramley & Co",
    description:
      "This contact opened or clicked the Spring Folder Push campaign email. Follow up while it's fresh.",
    customer_id: "cust_012",
    person_id: "per_001",
    subject_type: "campaign_engaged",
    subject_id: "cmp_001:per_001",
    subject_label: "Spring Folder Push",
    assigned_to_user_id: "user_001",
    created_by_user_id: "system",
    action_type: "call",
    due_date: null,
    created_at: `${daysAgo(7)}T08:00:00Z`,
    priority: "urgent",
    category_id: "cat_sales_followup",
    resolved_at: `${daysAgo(6)}T14:00:00Z`,
    closed_at: null,
    status: "resolved",
  },
  // Holiday Parks 2026 campaign contact tickets (direct-mail campaigns): one
  // per contact per campaign, raised by Call round 1. Their campaign side
  // (sends, outcome) is mockCampaignTicketContext in direct-mail-campaigns.ts.
  // ticket_hp26_per_hp_01_after_click was raised when Sarah clicked the send
  // after her first ticket was closed.
  ...[
    { person: "per_hp_01", name: "Sarah Okafor", resolved: true },
    { person: "per_hp_02", name: "Tom Brennan", resolved: false },
    { person: "per_hp_03", name: "Priya Shah", resolved: false },
    { person: "per_hp_04", name: "Mark Ellis", resolved: false },
  ].map(({ person, name, resolved }): Ticket => ({
    id: `ticket_hp26_${person}`,
    title: `Call ${name} — Holiday Parks 2026`,
    description:
      "Call round 1 – VIP follow-up: call this contact about the TIME pack.",
    person_id: person,
    subject_type: "campaign_contact",
    subject_id: `cmp_hp26:${person}`,
    subject_label: "Holiday Parks 2026",
    assigned_to_user_id: "user_002",
    created_by_user_id: "system",
    action_type: "call",
    due_date: "2026-10-28",
    created_at: "2026-10-27T08:00:00Z",
    priority: "normal",
    category_id: "cat_sales_followup",
    resolved_at: resolved ? "2026-10-28T11:30:00Z" : null,
    closed_at: null,
    status: resolved ? "resolved" : "open",
  })),
  {
    id: "ticket_hp26_per_hp_01_after_click",
    title: "Call Sarah Okafor — Holiday Parks 2026",
    description: "Sarah clicked the Holiday Parks 2026 email. Follow up.",
    person_id: "per_hp_01",
    subject_type: "campaign_contact",
    subject_id: "cmp_hp26:per_hp_01",
    subject_label: "Holiday Parks 2026",
    assigned_to_user_id: "user_002",
    created_by_user_id: "system",
    action_type: "call",
    due_date: "2026-10-30",
    created_at: "2026-10-29T15:00:00Z",
    priority: "urgent",
    category_id: "cat_sales_followup",
    resolved_at: null,
    closed_at: null,
    status: "open",
  },
];
